'use client';

import { ArrowLeftIcon } from 'lucide-react';
import type { Route } from 'next';
import { usePathname, useSearchParams } from 'next/navigation';
import { ButtonLink } from '@/components/ui/button-link';

type TPageHeader = {
  title: string;
  options?: React.ReactNode[];
  ignoreSearchParams?: boolean;
  customSearchParams?: string;
};

function getParentPath(pathname: string) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length <= 1) return '/';
  return `/${segments.slice(0, -1).join('/')}`;
}

const PageHeader = ({ title, options, ignoreSearchParams = false, customSearchParams }: TPageHeader) => {
  const pathname = usePathname();
  const searchParams = useSearchParams().toString();
  const previousPath = getParentPath(pathname);
  const sp = ignoreSearchParams
    ? ''
    : customSearchParams
      ? `${customSearchParams}`
      : searchParams
        ? `?${searchParams}`
        : '';

  return (
    <header className='bg-background sticky top-app-nav z-20 w-full print:hidden'>
      <nav className='border-border mobile:px-4 flex h-page-bar items-center justify-between border-b px-6'>
        <div className='w-20 -translate-x-2'>
          <ButtonLink
            href={`${previousPath}${sp}` as Route}
            variant='ghost'
            size='icon'
            className='rounded-full'
          >
            <ArrowLeftIcon
              aria-label='Gå tillbaka'
              size={18}
              strokeWidth={1.5}
              className='pointer-events-none'
            />
          </ButtonLink>
        </div>
        <h1 className='font-display w-full truncate text-center text-base'>{title}</h1>
        <div className='flex w-20 translate-x-2 justify-end gap-1'>
          {options?.map((element, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: options are arbitrary ReactNodes with no stable id; list is static and never reordered
            <div key={i}>{element}</div>
          ))}
        </div>
      </nav>
    </header>
  );
};

export default PageHeader;
